import {
  isPlatformBrowser,
  NgTemplateOutlet,
  ViewportScroller
} from "@angular/common";
import {
  Component,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation
} from "@angular/core";
import { AwsImage, CmsData } from "@dotcom-frontend/data";

import {
  LgGridColDirective,
  LgGridContainerDirective
} from "@legal-and-general/canopy";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { WysiwygDirective } from "../../../directives/wysiwyg.directive";
import { ComponentLoaderService } from "../../../services/component-loader/component-loader.service";
import { GlobalState } from "../../../store/global/global.reducer";
import { selectPageTitle } from "../../../store/global/global.selectors";

import { ImageComponent } from "../../globals/Image/Image.component";
import { DynamicComponent } from "../../utils/dynamic-loader/dynamic.component";
import { LoaderComponent } from "../../utils/dynamic-loader/loader.component";
import { SafeHtmlPipe } from "../../utils/pipes/safe-html..pipe";

@Component({
  selector: "dotcom-frontend-hero-block",
  templateUrl: "./HeroBlock.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrl: "./HeroBlock.component.scss",
  standalone: true,
  imports: [
    LgGridContainerDirective,
    LgGridColDirective,
    ImageComponent,
    LoaderComponent,
    WysiwygDirective,
    SafeHtmlPipe,
    NgTemplateOutlet
  ]
})
export class HeroBlockComponent implements DynamicComponent, OnInit {
  @Input() data: CmsData | undefined;
  isBrowser = isPlatformBrowser(this.platformId);
  heroTitle: string | undefined;
  heroSubTitle: string | undefined;
  image: AwsImage | undefined;
  heroStyle = "";
  heroBoxColor = "";
  contentRight = false;
  croppedImage = false;
  isSecondaryImage = false;
  contentBgColor = "bg-color-";
  ctaButtons: any;
  isLinkList = false;
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  pageTitle = "";
  heroOverlapContent: string | undefined;
  heroOverlapBlocks: any;
  mainContentAnchorText: string | undefined;
  showFindOutMoreLink = false;

  isHeroTabLinks = false;
  isGridOverlapBlock = false;
  hasAnimation = false;
  animate = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private store: Store<{ global: GlobalState }>,
    private componentLoaderService: ComponentLoaderService,
    private viewportScroller: ViewportScroller
  ) {
    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });
  }

  async ngOnInit() {
    if (this.data) {
      this.heroTitle = this.data["heroTitle"];
      this.heroSubTitle = this.data["heroSubTitle"];
      this.image = this.data["heroImage"];
      this.heroStyle = this.data["heroStyle"];
      this.heroBoxColor = this.data["heroBoxColour"];
      this.mainContentAnchorText = this.data["mainContentAnchorText"];
      this.isLinkList = this.data["isLinkList"];
      this.hasAnimation = this.data["heroContentAnimation"];

      if (this.data["heroOverlapBlocks"]) {
        this.heroOverlapContent =
          this.data["heroOverlapBlocks"][0]["contentLink"]["expanded"][
            "contentType"
          ][1];
      }

      if (this.data["showFindOutMoreLink"]) {
        this.showFindOutMoreLink = true;
      }

      if (this.heroOverlapContent === "GridBoxBlock") {
        this.isGridOverlapBlock = true;
      } else if (this.heroOverlapContent === "TabFilterBlock") {
        this.isHeroTabLinks = true;
      }

      if (this.data["heroOverlapBlocks"]) {
        this.heroOverlapBlocks =
          await this.componentLoaderService.contentAreaLoader(
            this.data["heroOverlapBlocks"],
            "HeroBlock"
          );
        this.heroOverlapBlocks = this.heroOverlapBlocks.slice(0, 1);
      }

      if (this.data["ctaButtons"]) {
        if (this.isLinkList) {
          this.ctaButtons = await this.componentLoaderService.loadComponent(
            "LinkList",
            "globals",
            {
              interactionContainer: "Hero Block",
              ctaButtons: this.data["ctaButtons"]
            }
          );
        } else {
          this.ctaButtons = [];
          for (const button of this.data["ctaButtons"].slice(0, 2)) {
            const buttonComponent =
              await this.componentLoaderService.loadComponent(
                "Button",
                "globals",
                {
                  href: button.href,
                  buttonStyle: button.buttonStyle,
                  text: button.text,
                  icon: button.icon,
                  target: button.target,
                  title: button.title,
                  extension: button.extension,
                  fileSize: button.fileSize,
                  adobeData: {
                    interactionContainer: "Hero Block",
                    interactionLink: button["href"],
                    interactionName: button["text"],
                    interactionPage: this.pageTitle,
                    interactionRegion: "",
                    interactionType: "ButtonClick",
                    interactionDetails: ""
                  }
                }
              );
            this.ctaButtons.push(buttonComponent);
          }
        }
      }

      this.contentRight =
        this.heroStyle === "primaryImage-RightBox" ||
        this.heroStyle === "primaryImageCroppedLeft-rightBox" ||
        this.heroStyle === "secondaryImage-RightBox";

      this.croppedImage =
        this.heroStyle === "primaryImageCroppedRight-leftBox" ||
        this.heroStyle === "primaryImageCroppedLeft-rightBox";

      this.isSecondaryImage =
        this.heroStyle === "secondaryImage-RightBox" ||
        this.heroStyle === "secondaryImage-LeftBox";

      this.contentBgColor =
        this.heroBoxColor !== "transparent"
          ? this.contentBgColor + this.heroBoxColor + "--transparent"
          : this.contentBgColor + this.heroBoxColor;

      if (this.isBrowser && this.hasAnimation) {
        setTimeout(() => {
          this.animate = true;
        }, 500);
      }
    }
  }

  scrollToMainContent() {
    this.viewportScroller.scrollToAnchor("main-content-anchor");
  }
}
